import { createReducer } from 'redux-starter-kit';
import {
    addPublishLocation, cancelEditLocation, editLocation,
    fetchAddLocation, fetchLocationPageData, fetchUpdateLocation,
    removePublishLocation,
} from './locationUiActions';
import { selectRequest } from '../../../domain/requests/RequestReducer';

/**
 * A selector for finding the current edit state.
 * @param {object} state The redux state for the application.
 * @returns An object containing details about the location being edited.
 * @example const editState = selectEditState(state);
 */
export const selectEditState = state => state.via.ui.locations.editState;

/**
 * A selector for finding the current publishing locations.
 * @param {object} state The redux state of the application.
 * @returns An array of location codes.
 * @example const loadingAreaCodes = selectLoadingDamageAreaCodes(state);
 */
export const selectLoadingActiveStatus = state => state.via.ui.locations.pendingPublishLocations;

/**
 * A selector that returns the state of the 'FETCH_LOCATIONS_PAGE_DATA' action.
 * @param {object} state The redux state for the application.
 * @return An object representing the request state.
 * @example const LocationRequest = selectRequestedLocations(state);
 */
export const selectLocationsPageDataRequest = (state) => {
    const requestId = state.via.ui.locations.requestLocationsPageDataId;
    if (!requestId) return null;
    return selectRequest(state, requestId);
};

/**
 * A selector for the current 'FETCH_CREATE_LOCATION' request.
 * @param {object} state The redux state for the application.
 * @returns An object representing the request state.
 * @example const addLocationsRequest = selectLocationRequest(state);
 */
export const selectSubmitLocationRequest = (state) => {
    const requestId = state.via.ui.locations.editState.requestSubmitLocationId;
    if (!requestId) return null;

    return selectRequest(state, requestId);
};



const initialEditingState = {
    isEditing: false,
    locationCode: null,
    requestSubmitLocationId: null,
    validationMessages: ({
        title: '',
        messages: [],
        visible: false,
    }),
};

const LocationsInitialState = {
    editState: initialEditingState, // Location being edited/created.
    pendingPublishLocations: [], // Location being submitted for publishing.
    requestLocationsPageDataId: null, // Request id for loading locations page data.
};

/**
 * A reducer for handling the UI state changes for the locations Configuration screen.
 */
const LocationReducer = createReducer(LocationsInitialState, {
    // The user has started editing the location.
    [editLocation]: (state, action) => ({
        ...state,
        editState: {
            ...state.editState,
            isEditing: true,
            locationCode: action.payload,
        },
    }),

    // The user has stopped editing the location.
    [cancelEditLocation]: state => ({
        ...state,
        editState: {
            ...initialEditingState,
        },
    }),

    // A new requst has been made to add a location.
    [fetchAddLocation]: (state, action) => ({
        ...state,
        editState: {
            ...state.editState,
            requestSubmitLocationId: action.payload,
        },
    }),

    // A new request has been made to update a location.
    [fetchUpdateLocation]: (state, action) => ({
        ...state,
        editState: {
            ...state.editState,
            requestSubmitLocationId: action.payload,
        },
    }),

    // A new request has been made to load all the locations page data
    [fetchLocationPageData]: (state, action) => ({
        ...state,
        requestLocationsPageDataId: action.payload,
    }),

    // A location is being published. So add it to state.
    [addPublishLocation]: (state, action) => ({
        ...state,
        pendingPublishLocations: [...state.pendingPublishLocations, action.payload],
    }),

    // A location has finished publishing. Remove it from state.
    [removePublishLocation]: (state, action) => ({
        ...state,
        pendingPublishLocations: state.pendingPublishLocations.filter(code => `${code}` !== `${action.payload}`),
    }),
});

export default LocationReducer;
